import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axios";
import { addFavorite, removeFavorite } from "../../services/favoriteService";
import { isAuthenticated } from "../../utils/auth";
import "../../styles/detail-property.css";
import DetailContactCard from "./DetailContactCard";
import DetailDescription from "./DetailDescription";
import DetailFacilities from "./DetailFacilities";
import DetailFeatures from "./DetailFeatures";
import DetailGallery from "./DetailGallery";
import DetailInfo from "./DetailInfo";
import DetailLocation from "./DetailLocation";
import DetailNearbyPlaces from "./DetailNearbyPlaces";
import DetailPropertySkeleton from "./DetailPropertySkeleton";
import DetailVirtualTours from "./DetailVirtualTours";

const NAVBAR_HEIGHT = 96;
const SIDEBAR_TOP_OFFSET = 24;

const DetailProperty = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const mainColRef = useRef(null);
  const sidebarColRef = useRef(null);
  const sidebarRef = useRef(null);
  const [sidebarMode, setSidebarMode] = useState("before");

  const fetchProperty = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axiosInstance.get(`/listing-properties/${slug}`);
      const data = response.data?.data || null;
      setProperty(data);
      if (data) {
        setIsFavorite(Boolean(data.is_favorite));
      }
    } catch (err) {
      console.error(err);
      setError("Property tidak ditemukan.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  useEffect(() => {
    const isDesktop = () => window.innerWidth >= 992;
    const handleScroll = () => {
      if (!isDesktop()) {
        setSidebarMode("before");
        return;
      }

      const mainCol = mainColRef.current;
      const sidebarCol = sidebarColRef.current;
      const sidebar = sidebarRef.current;

      if (!mainCol || !sidebarCol || !sidebar) return;

      const scrollY = window.scrollY;
      const mainRect = mainCol.getBoundingClientRect();
      const sidebarRect = sidebar.getBoundingClientRect();

      const mainTop = scrollY + mainRect.top;
      const mainBottom = scrollY + mainRect.bottom;
      const sidebarHeight = sidebarRect.height;
      const fixedStart = mainTop - NAVBAR_HEIGHT - SIDEBAR_TOP_OFFSET;
      const fixedEnd = mainBottom - sidebarHeight - NAVBAR_HEIGHT - SIDEBAR_TOP_OFFSET;

      if (scrollY < fixedStart) {

        setSidebarMode("before");
      } else if (scrollY >= fixedStart && scrollY <= fixedEnd) {

        setSidebarMode("fixed");
      } else {

        setSidebarMode("after");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [property]);

  const images = useMemo(() => {
    return Array.isArray(property?.images) ? property.images : [];
  }, [property]);

  const handleToggleFavorite = useCallback(async () => {
    if (!isAuthenticated()) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    if (favoriteLoading || !property) return;

    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await removeFavorite(property.id);
        setIsFavorite(false);
      } else {
        await addFavorite(property.id);
        setIsFavorite(true);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (err?.response?.status === 409) {
        setIsFavorite(true);
      }
      console.error('Favorite toggle error:', msg || err);
    } finally {
      setFavoriteLoading(false);
    }
  }, [isFavorite, favoriteLoading, property]);

  if (loading) return <DetailPropertySkeleton />;

  if (error || !property) {
    return (
      <Container className="detail-property-shell detail-property-page">
        <Alert variant="danger" className="mb-0">
          {error || "Property tidak ditemukan."}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="detail-property-shell detail-property-page">
      <div className="detail-property-layout">


        <Row className="g-4">
          <Col xs={12}>
            <div className="detail-gallery-wrapper">
              <DetailGallery images={images} title={property.title} />
              <button
                type="button"
                className={`detail-favorite-btn ${isFavorite ? 'is-favorite' : ''}`}
                onClick={handleToggleFavorite}
                disabled={favoriteLoading}
                aria-label={isFavorite ? 'Hapus dari favorit' : 'Simpan ke favorit'}
                title={isFavorite ? 'Hapus dari favorit' : 'Simpan ke favorit'}
              >
                {isFavorite ? <FaHeart aria-hidden="true" /> : <FaRegHeart aria-hidden="true" />}
              </button>
            </div>
          </Col>
        </Row>


        <Row className="g-4 detail-info-contact-row">


          <Col lg={8} xl={8} className="detail-main-col" ref={mainColRef}>
            <div className="detail-main-stack">
              <DetailInfo property={property} className="detail-info-card" />

              <DetailFeatures features={property.features || []} />
              <DetailFacilities
                houseFacilities={property.house_facilities || property.facilities || []}
                projectFacilities={property.project_facilities || []}
              />
              <DetailNearbyPlaces nearbyPlaces={property.nearby_places || []} />
              <DetailVirtualTours virtualTours={property.virtual_tours || []} />
              <DetailDescription property={property} />
              <DetailLocation property={property} />

            </div>
          </Col>

          <Col
            lg={4}
            xl={4}
            className="detail-sidebar-col"
            ref={sidebarColRef}
          >
            <div className="detail-sidebar-wrapper">

              <div
                ref={sidebarRef}
                className={`detail-sidebar-inner detail-sidebar-inner--${sidebarMode}`}
                style={
                  sidebarMode === "fixed"
                    ? {
                        width: sidebarColRef.current
                          ? sidebarColRef.current.getBoundingClientRect().width
                          : "100%",
                        top: NAVBAR_HEIGHT + SIDEBAR_TOP_OFFSET,
                      }
                    : undefined
                }
              >
                <DetailContactCard property={property} />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default DetailProperty;
